import { Link, useParams, Navigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { StoryStage } from '@/components/marketing/StoryStage';
import { COURSE_HREF, DIAGNOSTIC_FACTS, MARKETING_CTAS } from '@/lib/marketingCtas';

type Role = 'students' | 'parents' | 'tutors' | 'sat';

interface Step {
  step: number;
  title: string;
  description: string;
}

interface RoleContent {
  title: string;
  subtitle: string;
  steps: Step[];
  /** Heading and line above the buttons at the foot of the page. */
  ctaHeading: string;
  ctaBody: string;
  cta: { text: string; href: string };
  learnMore: { text: string; href: string };
}

const ROLE_CONTENT: Record<Role, RoleContent> = {
  students: {
    title: 'How tutoring works for students',
    subtitle: 'Tell us what you need, agree a time, and meet online. Every session ends with written notes.',
    steps: [
      {
        step: 1,
        title: 'Say what you need',
        description: 'Pick a subject and describe what you are working on. More detail means a better match.',
      },
      {
        step: 2,
        title: 'Mentara finds you a tutor',
        description:
          'Your request is matched with an available tutor. After your first session you can ask for the same tutor again.',
      },
      {
        step: 3,
        title: 'Agree a time',
        description:
          'Your tutor proposes a time. You accept it or suggest another, and nothing is booked until you both agree.',
      },
      {
        step: 4,
        title: 'Join online',
        description: 'When you both agree, the session gets a Google Meet link and a calendar entry. Open it when it is time.',
      },
      {
        step: 5,
        title: 'Read your notes',
        description:
          'After each session your tutor writes up the topics covered, your homework and what to work on next. Past notes stay with the session in your Sessions list.',
      },
      {
        step: 6,
        title: 'Pay once it has happened',
        description:
          'You pay by card when the session is complete. Cancel before a session happens and you are not charged.',
      },
    ],
    ctaHeading: 'Tell us what you need.',
    ctaBody: 'Studying for the SAT? The free SAT course works on its own, with no account and no tutor.',
    cta: { text: MARKETING_CTAS.students.label, href: MARKETING_CTAS.students.href },
    learnMore: { text: 'Free SAT course', href: COURSE_HREF },
  },
  parents: {
    title: 'How Mentara works for parents',
    subtitle: 'Link to your student, see their sessions and the tutor\'s notes, and keep the control where it belongs: with them.',
    steps: [
      {
        step: 1,
        title: 'Create a parent account',
        description: 'Sign up and choose Parent. Then you link to your student, which takes their invite code.',
      },
      {
        step: 2,
        title: 'Ask your student for an invite code',
        description:
          'Your student creates an invite code in their account and you enter it in yours. The code works once and expires after 7 days.',
      },
      {
        step: 3,
        title: 'Your student sets the limits',
        description:
          'Permissions are per link. They choose whether you see sessions and whether you join the tutor chat, and they can revoke the link at any time.',
      },
      {
        step: 4,
        title: 'See sessions and notes',
        description:
          'You see upcoming sessions, and after each one the tutor\'s written notes: topics covered, homework and the next focus. A missed session shows as not attended.',
      },
      {
        step: 5,
        title: 'Choose your alerts',
        description: 'Booked, completed and cancelled alerts are on by default. You choose which ones to keep.',
      },
      {
        step: 6,
        title: 'Book or pay when they allow it',
        description:
          'If your student allows it, you can book and pay for sessions on their behalf. Whatever you do for them is logged and they are told.',
      },
    ],
    ctaHeading: 'Create a parent account.',
    ctaBody: 'The free SAT course is for your student. They can use it without an account and show you their course map.',
    cta: { text: MARKETING_CTAS.parents.label, href: MARKETING_CTAS.parents.href },
    learnMore: { text: 'What parents can see', href: '/what-we-do/parents/' },
  },
  tutors: {
    title: 'How Mentara works for tutors',
    subtitle: 'Requests, agreed times, your students and each session\'s payment, in one place.',
    steps: [
      {
        step: 1,
        title: 'Get a tutor account',
        description:
          'Tutor accounts are created by the Mentara team. Email admin@mentaratutors.com with the subjects you teach.',
      },
      {
        step: 2,
        title: 'Requests come to you',
        description:
          'Students request a subject and Mentara matches each request with an available tutor. Students can also ask for a tutor they have already worked with.',
      },
      {
        step: 3,
        title: 'Agree a time in the app',
        description:
          'Accept a proposed time or counter it. Both of you confirm before a session is scheduled. Weekly series are your call: approve, approve with exceptions, or decline.',
      },
      {
        step: 4,
        title: 'Teach online',
        description: 'Each scheduled session gets a Google Meet link and a calendar entry, made for you.',
      },
      {
        step: 5,
        title: 'Write the notes, keep your students together',
        description:
          'After each session you write structured feedback: topics, homework and next focus. Your students sit in one list, and for SAT students you can see their results and assign a diagnostic or a practice set.',
      },
      {
        step: 6,
        title: 'Get paid',
        description:
          'Students pay by card through Stripe. Your payments page shows each session, its status and your share. Payouts are arranged with the Mentara team.',
      },
    ],
    ctaHeading: 'Bring your subjects.',
    ctaBody: 'Tutor accounts are set up by the Mentara team. Email us with the subjects you teach.',
    cta: { text: 'Email us to apply', href: 'mailto:admin@mentaratutors.com' },
    learnMore: { text: 'What tutors get', href: '/what-we-do/tutors/' },
  },
  sat: {
    title: 'How the free SAT course works',
    subtitle: `Start with a ${DIAGNOSTIC_FACTS.questions}-question diagnostic, no account needed. The course re-orders around you, and each area turns green once you have proved it.`,
    steps: [
      {
        step: 1,
        title: 'Take the diagnostic',
        description: `${DIAGNOSTIC_FACTS.questions} questions across all eight SAT areas. It takes ${DIAGNOSTIC_FACTS.minutesLabel}, it is free, and you do not need an account.`,
      },
      {
        step: 2,
        title: 'Your course re-orders',
        description:
          'When the diagnostic ends, your eight areas are sorted by how you did. The weakest comes first, so the course starts where you need it most.',
      },
      {
        step: 3,
        title: 'Practice your weakest area',
        description:
          'Practice sets aim at that area. Check an answer as you go and see right away whether it is correct and why.',
      },
      {
        step: 4,
        title: 'Prove it and it turns green',
        description:
          'When you can show the area holds up, it turns green. Then the course moves to the next one.',
      },
      {
        step: 5,
        title: 'Set a baseline in Bluebook',
        description:
          "Take a full-length practice test in College Board's Bluebook, timed like test day, and enter your score. It becomes your baseline, and later tests show how far you have come.",
      },
      {
        step: 6,
        title: 'Add a tutor if you want one',
        description:
          'The course works on its own. When you want a person, add 1-on-1 SAT sessions with a tutor who can start from your results. Tutoring is paid and optional.',
      },
    ],
    ctaHeading: 'Start with the diagnostic.',
    ctaBody: 'No account needed. Nothing is saved. Create a free account to keep your progress.',
    cta: { text: MARKETING_CTAS.howItWorksSatPrimary.label, href: MARKETING_CTAS.howItWorksSatPrimary.href },
    learnMore: { text: MARKETING_CTAS.howItWorksSatSecondary.label, href: MARKETING_CTAS.howItWorksSatSecondary.href },
  },
};

const VALID_ROLES: Role[] = ['students', 'parents', 'tutors', 'sat'];

export function HowItWorksPage() {
  const { role } = useParams<{ role: string }>();

  if (!role || !VALID_ROLES.includes(role as Role)) {
    return <Navigate to="/how-it-works/students/" replace />;
  }

  const content = ROLE_CONTENT[role as Role];
  const isSat = role === 'sat';

  // SEO meta tags based on role
  const seoMeta = {
    students: {
      title: 'How Tutoring Works for Students | Mentara',
      description: 'See how tutoring works on Mentara: say what you need, get matched, agree a time, join online and read your tutor\'s notes after every session.',
      keywords: 'how online tutoring works, 1-on-1 tutoring, request a tutor, student tutoring guide',
    },
    parents: {
      title: 'How Mentara Works for Parents | Mentara',
      description: 'See how parents use Mentara: link to your student with an invite code, see their sessions and tutor notes, and choose your alerts. Your student sets the limits.',
      keywords: 'parent account, tutoring visibility, tutor feedback, link to my child, tutoring for parents',
    },
    tutors: {
      title: 'How Mentara Works for Tutors | Mentara',
      description: 'See how tutoring on Mentara works: matched requests, agreed times, Google Meet links, session notes, your student list and payments through Stripe.',
      keywords: 'tutor accounts, tutoring platform, session payments, tutor tools, online tutoring platform',
    },
    sat: {
      title: 'How the Free SAT Course Works | Mentara',
      description: `See how the free SAT course works: take a ${DIAGNOSTIC_FACTS.questions}-question diagnostic with no account, practice your weakest area, prove it, and set a Bluebook baseline. Add a 1-on-1 tutor when you want one.`,
      keywords: 'how the free SAT course works, free SAT prep, SAT diagnostic, digital SAT practice, Bluebook practice test, SAT tutoring',
    },
  }[role as Role];

  return (
    <>
      <title>{seoMeta.title}</title>
      <meta name="description" content={seoMeta.description} />
      <meta name="keywords" content={seoMeta.keywords} />
      <link rel="canonical" href={`https://mentaratutors.com/how-it-works/${role}/`} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={`https://mentaratutors.com/how-it-works/${role}/`} />
      <meta property="og:title" content={seoMeta.title} />
      <meta property="og:description" content={seoMeta.description} />
      <meta property="og:image" content="https://mentaratutors.com/logos/mentara-full-light.png" />

      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={`https://mentaratutors.com/how-it-works/${role}/`} />
      <meta property="twitter:title" content={seoMeta.title} />
      <meta property="twitter:description" content={seoMeta.description} />
      <meta property="twitter:image" content="https://mentaratutors.com/logos/mentara-full-light.png" />

      <div className="bg-background">
      {/* Hero */}
      <section className="px-4 pb-8 pt-14 md:pb-10 md:pt-20">
        <div className="container max-w-3xl mx-auto text-center">
          <h1 className="text-4xl font-bold tracking-tight md:text-5xl leading-tight">
            {content.title}
          </h1>
          <p className="mt-6 text-lg text-muted-foreground max-w-2xl mx-auto">
            {content.subtitle}
          </p>
        </div>
      </section>

      {/* Story Stage — the free SAT course's four-frame arc, above its six steps */}
      {isSat && (
        <section className="px-4 pb-14">
          <div className="container max-w-5xl mx-auto">
            <StoryStage />
          </div>
        </section>
      )}

      {/* Steps */}
      <section className="py-16 px-4 border-t border-border/40">
        <div className="container max-w-3xl mx-auto space-y-10">
          {content.steps.map(({ step, title, description }) => (
            <div key={step} className="flex gap-6">
              <div className="flex-shrink-0 flex items-start">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary text-primary-foreground font-bold text-lg">
                  {step}
                </div>
              </div>
              <div className="space-y-2 pt-1">
                <h3 className="text-xl font-semibold">{title}</h3>
                <p className="text-muted-foreground leading-relaxed">{description}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 border-t border-border/40">
        <div className="container max-w-3xl mx-auto text-center space-y-6">
          <h2 className="text-3xl font-bold tracking-tight">{content.ctaHeading}</h2>
          <p className="text-lg text-muted-foreground">{content.ctaBody}</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button size="lg" asChild>
              {content.cta.href.startsWith('mailto:') ? (
                <a href={content.cta.href}>{content.cta.text}</a>
              ) : (
                <Link to={content.cta.href}>{content.cta.text}</Link>
              )}
            </Button>
            <Button size="lg" variant="outline" asChild>
              <Link to={content.learnMore.href}>{content.learnMore.text}</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
    </>
  );
}
